import { NavLink } from 'react-router-dom'
import { useState } from 'react'
import {
  Home,
  Building2,
  FileText,
  DollarSign,
  LogOut,
  Globe,
  Menu,
  X,
} from 'lucide-react'
import Logo from '../common/Logo'
import { NotificationBell } from '../notification'

interface MenuItem {
  icon: typeof Home
  label: string
  path: string
}

const menuItems: MenuItem[] = [
  { icon: Home, label: 'หน้าหลัก', path: '/' },
  { icon: Building2, label: 'ฟาร์ม', path: '/farms' },
  { icon: FileText, label: 'รายงาน', path: '/report' },
  { icon: DollarSign, label: 'การเงิน', path: '/finance' },
]

export function Sidebar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn')
    localStorage.removeItem('userPhone')
    window.location.href = '/login'
  }

  const currentDate = new Date().toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return (
    <>
      <nav className="bg-white shadow-md border-b border-border-light fixed top-0 left-0 right-0 z-50">
        <div className="px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo Section - ซ้ายสุด */}
            <div className="flex items-center gap-4 lg:gap-8">
              <NavLink to="/" className="flex items-center hover:opacity-80 transition-opacity">
                <Logo size="sm" variant="full" showText={true} />
              </NavLink>

              {/* Navigation Menu - แนวนอน (Desktop only) */}
              <div className="hidden lg:flex items-center gap-2">
                {menuItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `
                      flex items-center gap-2
                      px-4 py-2 rounded-lg
                      transition-all duration-200
                      ${
                        isActive
                          ? 'bg-primary-800 text-white'
                          : 'text-text-secondary hover:bg-background-tertiary hover:text-primary-800'
                      }
                    `
                    }
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-sm font-medium whitespace-nowrap">
                      {item.label}
                    </span>
                  </NavLink>
                ))}
              </div>
            </div>

            {/* Right Section - Actions */}
            <div className="flex items-center gap-2 lg:gap-4">
              {/* Language Selector (Hidden on mobile) */}
              <button className="hidden lg:flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-background-tertiary transition-colors">
                <Globe className="w-5 h-5 text-text-secondary" />
                <span className="hidden lg:inline text-sm text-text-secondary font-medium">
                  ภาษาไทย
                </span>
              </button>

              {/* Notification Bell */}
              <NotificationBell />

              {/* User Info (Hidden on mobile and tablet) */}
              <div className="hidden lg:flex items-center gap-3 pl-4 border-l border-border-light">
                <div className="text-right">
                  <p className="text-sm font-medium text-text-primary">
                    ติดต่อเรา
                  </p>
                  <p className="text-xs text-text-tertiary">{currentDate}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-primary-800 flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">S</span>
                </div>
              </div>

              {/* Logout Button (Desktop only) */}
              <button
                onClick={handleLogout}
                className="
                  hidden lg:flex items-center gap-2
                  px-3 py-2 rounded-lg
                  text-text-secondary hover:bg-error-light hover:text-error
                  transition-all duration-200
                "
                title="ออกจากระบบ"
              >
                <LogOut className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-sm font-medium whitespace-nowrap">
                  ออกจากระบบ
                </span>
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-background-tertiary transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-text-secondary" />
                ) : (
                  <Menu className="w-6 h-6 text-text-secondary" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border-light bg-white">
            <div className="px-4 py-4 space-y-2">
              {/* Mobile Navigation */}
              {menuItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `
                    flex items-center gap-3
                    px-4 py-3 rounded-lg
                    transition-all duration-200
                    ${
                      isActive
                        ? 'bg-primary-800 text-white'
                        : 'text-text-secondary hover:bg-background-tertiary hover:text-primary-800'
                    }
                  `
                  }
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  <span className="text-sm font-medium">
                    {item.label}
                  </span>
                </NavLink>
              ))}

              {/* Mobile User Info */}
              <div className="flex items-center gap-3 px-4 py-3 border-t border-border-light mt-2 pt-4">
                <div className="w-10 h-10 rounded-full bg-primary-800 flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">S</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-text-primary">
                    ติดต่อเรา
                  </p>
                  <p className="text-xs text-text-tertiary">{currentDate}</p>
                </div>
              </div>

              {/* Mobile Language */}
              <button className="flex items-center gap-3 w-full px-4 py-3 rounded-lg hover:bg-background-tertiary transition-colors text-left">
                <Globe className="w-5 h-5 text-text-secondary" />
                <span className="text-sm text-text-secondary font-medium">
                  ภาษาไทย
                </span>
              </button>

              {/* Mobile Logout */}
              <button
                onClick={handleLogout}
                className="
                  flex items-center gap-3 w-full
                  px-4 py-3 rounded-lg
                  text-text-secondary hover:bg-error-light hover:text-error
                  transition-all duration-200
                "
              >
                <LogOut className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-sm font-medium">
                  ออกจากระบบ
                </span>
              </button>
            </div>
          </div>
        )}
      </nav>
    </>
  )
}

export default Sidebar
